import type { SceneCanvasState } from "../scene/model";

/** Reuse equal immutable values, without changing either input or the stored snapshot. */
function share<T>(current: T, restored: T): T {
  if (current === restored) return current;
  if (
    !current ||
    !restored ||
    typeof current !== "object" ||
    typeof restored !== "object" ||
    Array.isArray(current) !== Array.isArray(restored)
  )
    return restored;
  if (Array.isArray(current) && Array.isArray(restored)) {
    const values = restored.map((value, i) => share(current[i], value));
    return (
      current.length === values.length &&
      values.every((v, i) => v === current[i])
        ? current
        : values
    ) as T;
  }
  const before = current as Record<string, unknown>,
    after = restored as Record<string, unknown>,
    keys = Object.keys(after);
  let equal = Object.keys(before).length === keys.length;
  const result: Record<string, unknown> = {};
  for (const key of keys) {
    const present = Object.hasOwn(before, key);
    const value = present ? share(before[key], after[key]) : after[key];
    Object.defineProperty(result, key, {
      value,
      enumerable: true,
      writable: true,
      configurable: true,
    });
    equal &&= present && value === before[key];
  }
  return equal ? current : (result as T);
}

function shareById<T extends { id: string }>(current: T[], restored: T[]): T[] {
  const byId = new Map(current.map((value) => [value.id, value]));
  const values = restored.map((value) => {
    const before = byId.get(value.id);
    return before ? share(before, value) : value;
  });
  return current.length === values.length &&
    values.every((v, i) => v === current[i])
    ? current
    : values;
}

/** Undo changes only edited values; unchanged objects/resources keep their display caches. */
export function restoreNativeHistory(
  current: SceneCanvasState,
  restored: SceneCanvasState,
): SceneCanvasState {
  return {
    ...restored,
    nodes: shareById(current.nodes, restored.nodes),
    assets: shareById(current.assets, restored.assets),
    size: share(current.size, restored.size),
    ...(restored.pageSettings
      ? { pageSettings: share(current.pageSettings, restored.pageSettings)! }
      : {}),
  };
}
